import { Space, SpaceVertical } from '@looker/components';
import { BIIcon } from '@pulsepointinc/bi-components';
import { DsCheckbox, DsTypography } from '@pulsepointinc/design-system-react';
import { type FC, useId } from 'react';
import type { Filter } from '../../../../../../stores/filters';
import { type URLParamFilterConfig, useCustomUrlParamFilter } from '../hooks';
import { ParamFilter } from '../ParamFilter';
import { StyledAddParameterButton, StyledIconWrapper, StyledLinkedFilterWrapper } from './styles';

interface CustomUrlParamsFilterProps {
  onChange: (filter: Filter) => void;
  paramConfig: URLParamFilterConfig;
}

const CustomUrlParamsFilter: FC<CustomUrlParamsFilterProps> = ({ onChange, paramConfig }) => {
  const {
    paramsDisplayed,
    paramsNotDisplayed,
    displayNewParam,
    removeParam,
    handleCheckboxChange,
    isParamsControlEnabled,
    setParamsDisplayed,
  } = useCustomUrlParamFilter({ filterConfig: paramConfig, onChange });

  const frequencyCheckboxId = useId();

  return (
    <SpaceVertical gap="u1">
      <Space gap="u2" align="center">
        <DsCheckbox
          id={frequencyCheckboxId}
          onCheckedChange={(event: CustomEvent<boolean>) => handleCheckboxChange(event.detail)}
          checked={isParamsControlEnabled}
        >
          <DsTypography as="span" variant="label">
            Custom URL Parameter
          </DsTypography>
        </DsCheckbox>
      </Space>

      {isParamsControlEnabled && (
        <SpaceVertical paddingLeft="u8" gap="u3">
          <DsTypography color="secondary">Only trigger if the pixel has specified parameter</DsTypography>

          <SpaceVertical gap="u2">
            {paramsDisplayed.map((paramKey, index) => {
              const { filterName } = paramConfig[paramKey];
              return (
                <SpaceVertical gap="u2" key={filterName}>
                  <StyledLinkedFilterWrapper>
                    <ParamFilter
                      filterName={filterName}
                      onChange={onChange}
                      paramOptions={paramsNotDisplayed}
                      paramKey={paramKey}
                      removeFilter={index ? () => removeParam(paramKey) : undefined}
                      onParamChange={(param) => {
                        setParamsDisplayed((prev: string[]) => {
                          const newParams = [...prev];
                          newParams[index] = param;
                          return newParams;
                        });
                      }}
                    />
                  </StyledLinkedFilterWrapper>
                </SpaceVertical>
              );
            })}
            {!!paramsNotDisplayed.length && (
              <StyledAddParameterButton
                color="invisible"
                size="small"
                onClick={displayNewParam}
                iconBefore={
                  <StyledIconWrapper>
                    <BIIcon icon={'Add'} />
                  </StyledIconWrapper>
                }
              >
                Add parameter
              </StyledAddParameterButton>
            )}
          </SpaceVertical>
        </SpaceVertical>
      )}
    </SpaceVertical>
  );
};

export default CustomUrlParamsFilter;
